import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  RefreshCw,
} from 'lucide-react';
import { NOTIF_TYPE_CONFIG } from '@/lib/notification-config';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';
import type { NotificationItem } from '@hancomum/shared';
import { formatDistanceToNow } from 'date-fns';
import { ko } from 'date-fns/locale';


interface Props {
  unreadCount: number;
  onCountChange: () => void;
}

export default function NotificationPopover({ unreadCount, onCountChange }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  /* ── 외부 클릭 닫기 ── */
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  /* ── 알림 목록 불러오기 ── */
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications', { params: { page: 1, limit: 20 } });
      setNotifications(res.data.data ?? []);
    } catch {
      toast.error('알림을 불러오지 못했습니다');
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── 열 때 데이터 갱신 ── */
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  /* ── 개별 읽음 처리 ── */
  async function handleRead(item: NotificationItem) {
    if (!item.is_read) {
      try {
        await api.patch(`/notifications/${item.id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)),
        );
        onCountChange();
      } catch {
        toast.error('알림 읽음 처리에 실패했습니다');
      }
    }
    if (item.link) {
      setOpen(false);
      navigate(item.link);
    }
  }

  /* ── 전체 읽음 ── */
  async function handleReadAll() {
    setMarkingAll(true);
    try {
      await api.patch('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      onCountChange();
    } catch {
      toast.error('전체 읽음 처리에 실패했습니다');
    } finally {
      setMarkingAll(false);
    }
  }

  return (
    <div className="relative" ref={popoverRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative p-2 rounded-md text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="알림"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-5 h-5 px-1 text-[11px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Popover */}
      {open && (
        <div className="fixed inset-x-3 top-14 sm:absolute sm:inset-auto sm:right-0 sm:top-auto sm:mt-2 w-auto sm:w-96 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl z-50 flex flex-col max-h-[70vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">알림</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleReadAll}
                  disabled={markingAll}
                  className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
                  title="모두 읽음으로 표시"
                >
                  <CheckCheck size={14} />
                  모두 읽음
                </button>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/settings?tab=notifications');
                }}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="알림 설정"
              >
                설정
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {loading ? (
              <div className="flex items-center justify-center py-10 gap-2 text-gray-400">
                <RefreshCw size={16} className="animate-spin" />
                <span className="text-sm">불러오는 중...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <Bell size={28} className="mb-2" />
                <p className="text-sm">알림이 없습니다</p>
              </div>
            ) : (
              <ul>
                {notifications.map((item) => {
                  const cfg = NOTIF_TYPE_CONFIG[item.type] ?? NOTIF_TYPE_CONFIG.system;
                  const Icon = cfg.icon;

                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => handleRead(item)}
                        className={cn(
                          'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors',
                          item.is_read
                            ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            : 'bg-blue-50/40 dark:bg-blue-900/20 hover:bg-blue-50/70 dark:hover:bg-blue-900/30',
                        )}
                      >
                        {/* Icon */}
                        <div
                          className={cn(
                            'flex items-center justify-center h-8 w-8 rounded-full shrink-0 mt-0.5',
                            cfg.bg,
                          )}
                        >
                          <Icon size={15} className={cfg.color} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p
                              className={cn(
                                'text-sm truncate',
                                item.is_read ? 'text-gray-700 dark:text-gray-300' : 'font-semibold text-gray-900 dark:text-gray-100',
                              )}
                            >
                              {item.title}
                            </p>
                            {!item.is_read && (
                              <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                            {item.message}
                          </p>
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                            {formatDistanceToNow(new Date(item.created_at), {
                              addSuffix: true,
                              locale: ko,
                            })}
                          </p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer — 전체 알림 이력 페이지 링크 */}
          <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2.5">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block text-center text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              전체 알림 보기
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
